import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Bell, BellRing, Inbox, Check, CheckCheck, Trash2, Filter,
  RefreshCw, ExternalLink, Brain, Shield, FileCheck, Package,
  ChevronRight, Settings, X
} from 'lucide-react';
import { api } from '../api/client';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';

const NotificationInboxPage = () => {
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadBySource, setUnreadBySource] = useState({});
  const [total, setTotal] = useState(0);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const eventSourceRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (readFilter === 'unread') params.append('is_read', 'false');
      if (readFilter === 'read') params.append('is_read', 'true');
      
      const response = await api.get(`/notifications/inbox?${params.toString()}`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
      setUnreadBySource(response.data.unread_by_source || {});
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [sourceFilter, readFilter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // SSE connection for real-time updates
  useEffect(() => {
    const connectSSE = () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) return;

        const eventSource = new EventSource(
          `${process.env.REACT_APP_BACKEND_URL}/api/notifications/inbox/stream`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        eventSource.addEventListener('notification', (event) => {
          const notification = JSON.parse(event.data);
          setNotifications(prev => [notification, ...prev]);
          setUnreadCount(prev => prev + 1);
          toast.info(notification.title, {
            description: notification.message?.slice(0, 50),
            action: notification.action_url ? {
              label: 'View',
              onClick: () => navigate(notification.action_url)
            } : undefined
          });
        });

        eventSource.onerror = () => {
          eventSource.close();
          // Reconnect after 5s
          setTimeout(connectSSE, 5000);
        };

        eventSourceRef.current = eventSource;
      } catch (error) {
        console.debug('SSE connection failed:', error);
      }
    };

    connectSSE();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [navigate]);

  const handleMarkRead = async (id, isRead) => {
    try {
      await api.put(`/notifications/inbox/${id}`, { is_read: !isRead });
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: !isRead } : n)
      );
      setUnreadCount(prev => isRead ? prev + 1 : prev - 1);
    } catch (error) {
      toast.error('Failed to update notification');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const params = sourceFilter !== 'all' ? `?source=${sourceFilter}` : '';
      await api.post(`/notifications/inbox/mark-all-read${params}`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/notifications/inbox/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Notification archived');
    } catch (error) {
      toast.error('Failed to archive notification');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedIds.length === 0) return;
    
    try {
      for (const id of selectedIds) {
        if (action === 'read') {
          await api.put(`/notifications/inbox/${id}`, { is_read: true });
        } else if (action === 'delete') {
          await api.delete(`/notifications/inbox/${id}`);
        }
      }
      fetchNotifications();
      setSelectedIds([]);
      toast.success(`${selectedIds.length} notifications updated`);
    } catch (error) {
      toast.error('Bulk action failed');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === notifications.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(notifications.map(n => n.id));
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'agentic_pm':
        return <Brain className="w-4 h-4" />;
      case 'governance':
        return <Shield className="w-4 h-4" />;
      case 'compliance':
        return <FileCheck className="w-4 h-4" />;
      case 'delivery':
        return <Package className="w-4 h-4" />;
      default:
        return <Bell className="w-4 h-4" />;
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'agentic_pm':
        return 'bg-purple-100 text-purple-800';
      case 'governance':
        return 'bg-blue-100 text-blue-800';
      case 'compliance':
        return 'bg-green-100 text-green-800';
      case 'delivery':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'urgent':
        return <Badge variant="destructive">Urgent</Badge>;
      case 'high':
        return <Badge className="bg-amber-100 text-amber-800">High</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-2">
            <Inbox className="w-7 h-7 text-[var(--clr-brand-primary)]" />
            Notification Center
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">
            {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleMarkAllRead} disabled={unreadCount === 0}>
            <CheckCheck className="w-4 h-4 mr-2" />
            Mark All Read
          </Button>
          <Button variant="outline" asChild>
            <Link to="/settings/notifications">
              <Settings className="w-4 h-4 mr-2" />
              Preferences
            </Link>
          </Button>
        </div>
      </div>

      {/* Source Tabs */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={sourceFilter === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSourceFilter('all')}
          data-testid="filter-all"
        >
          All
          {unreadCount > 0 && (
            <Badge className="ml-2" variant="secondary">{unreadCount}</Badge>
          )}
        </Button>
        {['agentic_pm', 'governance', 'compliance', 'delivery'].map((source) => (
          <Button
            key={source}
            variant={sourceFilter === source ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSourceFilter(source)}
            data-testid={`filter-${source}`}
          >
            {getSourceIcon(source)}
            <span className="ml-2 capitalize">{source.replace('_', ' ')}</span>
            {(unreadBySource[source] || 0) > 0 && (
              <Badge className="ml-2" variant="secondary">{unreadBySource[source]}</Badge>
            )}
          </Button>
        ))}
      </div>

      {/* Filters & Bulk Actions */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Checkbox
                checked={selectedIds.length === notifications.length && notifications.length > 0}
                onCheckedChange={toggleSelectAll}
                data-testid="select-all"
              />
              <Select value={readFilter} onValueChange={setReadFilter}>
                <SelectTrigger className="w-32" data-testid="read-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="unread">Unread</SelectItem>
                  <SelectItem value="read">Read</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-[var(--clr-text-secondary)]">
                  {selectedIds.length} selected
                </span>
                <Button variant="outline" size="sm" onClick={() => handleBulkAction('read')}>
                  <Check className="w-4 h-4 mr-1" />
                  Mark Read
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleBulkAction('delete')}>
                  <Trash2 className="w-4 h-4 mr-1" />
                  Archive
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Notifications List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[var(--clr-brand-primary)]" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-[var(--clr-text-secondary)]">
              <BellRing className="w-12 h-12 mb-4 opacity-50" />
              <p>No notifications</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-4 p-4 hover:bg-[var(--clr-surface-subtle)] transition-colors ${
                    !notification.is_read ? 'bg-blue-50/50' : ''
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <Checkbox
                    checked={selectedIds.includes(notification.id)}
                    onCheckedChange={() => toggleSelect(notification.id)}
                  />

                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${getSourceColor(notification.source)}`}>
                    {getSourceIcon(notification.source)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`font-medium ${!notification.is_read ? 'text-[var(--clr-text-primary)]' : 'text-[var(--clr-text-secondary)]'}`}>
                          {notification.title}
                        </p>
                        <p className="text-sm text-[var(--clr-text-secondary)] mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {getPriorityBadge(notification.priority)}
                        {!notification.is_read && (
                          <div className="w-2 h-2 rounded-full bg-blue-500" />
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-xs text-[var(--clr-text-secondary)]">
                        {notification.created_at ? new Date(notification.created_at).toLocaleString() : ''}
                      </span>
                      <Badge variant="outline" className="text-xs capitalize">
                        {notification.source?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    {notification.action_url && (
                      <Button variant="ghost" size="sm" asChild>
                        <Link to={notification.action_url}>
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMarkRead(notification.id, notification.is_read)}
                    >
                      {notification.is_read ? (
                        <Bell className="w-4 h-4" />
                      ) : (
                        <Check className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(notification.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination info */}
      {total > notifications.length && (
        <p className="text-center text-sm text-[var(--clr-text-secondary)]">
          Showing {notifications.length} of {total} notifications
        </p>
      )}
    </div>
  );
};

export default NotificationInboxPage;
